import invariant from "tiny-invariant";
import { AggregateRoot, CommandHandlerFunction } from "@lookiero/messaging";
import { ResetCheckout } from "../command/resetCheckout";
import { StartCheckout } from "../command/startCheckout";
import { SubmitCheckout } from "../command/submitCheckout";
import { checkoutNotModifiable } from "./checkoutNotModifiable";
import { checkoutReset } from "./checkoutReset";
import { checkoutStarted } from "./checkoutStarted";
import { checkoutSubmitted } from "./checkoutSubmitted";

enum CheckoutStatus {
  AVAILABLE = "AVAILABLE",
  NOTIFIED = "NOTIFIED",
  STARTED = "STARTED",
  SUBMITTED = "SUBMITTED",
  COMPLETED = "COMPLETED",
}

interface Checkout extends AggregateRoot {
  readonly status: CheckoutStatus;
  readonly customerId: string;
  readonly boxId: string;
  readonly checkoutBookingId: string;
}

const startCheckoutHandler: CommandHandlerFunction<StartCheckout, Checkout> =
  () =>
  async ({ aggregateRoot, command }) => {
    const { aggregateId } = command;

    invariant(aggregateRoot.status !== CheckoutStatus.STARTED, "Checkout is already started");
    invariant(aggregateRoot.status !== CheckoutStatus.SUBMITTED, "Checkout is already submitted");
    invariant(aggregateRoot.status !== CheckoutStatus.COMPLETED, "Checkout is already completed");

    return {
      ...aggregateRoot,
      status: CheckoutStatus.STARTED,
      domainEvents: [checkoutStarted({ aggregateId })],
    };
  };

const submitCheckoutHandler: CommandHandlerFunction<SubmitCheckout, Checkout> =
  () =>
  async ({ aggregateRoot, command }) => {
    const { aggregateId } = command;

    invariant(aggregateRoot.status !== CheckoutStatus.COMPLETED, "Checkout is already completed");

    return {
      ...aggregateRoot,
      status: CheckoutStatus.SUBMITTED,
      domainEvents: [checkoutSubmitted({ aggregateId })],
    };
  };

/**
 * A checkout which is already submitted or completed cannot be reset. Deciding here, against the
 * freshly read aggregate, keeps the request off the wire; the backend still rejects the race.
 */
const resetCheckoutHandler: CommandHandlerFunction<ResetCheckout, Checkout> =
  () =>
  async ({ aggregateRoot, command }) => {
    const { aggregateId } = command;
    const isModifiable = ![CheckoutStatus.SUBMITTED, CheckoutStatus.COMPLETED].includes(aggregateRoot.status);

    return {
      ...aggregateRoot,
      domainEvents: [isModifiable ? checkoutReset({ aggregateId }) : checkoutNotModifiable({ aggregateId })],
    };
  };

export type { Checkout };
export { CheckoutStatus, resetCheckoutHandler, startCheckoutHandler, submitCheckoutHandler };
